import { Injectable } from '@angular/core';
import {Card, CardExtension, CardIssueResultDto, CardWithSecondaries} from "../../dto/card";
import { CrudService } from '../../services';
import { HttpParams, HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DataListModel } from '../../models/data-list-model';
import { toServerDateString } from '../../providers/app-date-adapter';
import {CardStatus} from '../../dto/cardStatus';
import {EnumModel} from '../../models/enum-model';
import {CardCancellationModel} from '../../models/card-cancellation-model';
import {GpsCardStatus} from '../../dto/gps-card-status';
import {CardCashbackSummary, CardDeclineStats, CardLimitStats, QuickBalance} from "../../dto/CardbroGateway/card-info";
import {Pin} from '../../dto/pin';
import {AnnualFeeInfoModel, CardActivityInfoModel, CardExpiryInfoModel} from '../../dto/cardInfoModelBase';
import {CardReorder} from '../../dto/cardReorder';

@Injectable({
  providedIn: 'root'
})
export class CardsService implements CrudService<Card> {
  readonly apiPath = 'cards/';

  constructor(private http: HttpClient) { }
  get(id: any) {
    return this.http.get(this.apiPath + id) as Observable<CardWithSecondaries>;
  }

  getStatis(){
    return this.http.get(this.apiPath + 'statis') as Observable<Array<CardStatus>>;
  }

  getCardLimitStats(cardId: number): Observable<CardLimitStats> {
    return this.http.get(this.apiPath + cardId + '/limit-stats') as Observable<CardLimitStats>;
  }

  getCardDeclineStats(cardId: number, start: moment.Moment, end: moment.Moment): Observable<CardDeclineStats> {
    return this.http.get(this.apiPath + cardId + '/decline-stats') as Observable<CardDeclineStats>;
  }

  getCardCashbackSummary(publicToken: string): Observable<CardCashbackSummary> {
    return this.http.get(this.apiPath + publicToken + '/cashback/summary') as Observable<CardCashbackSummary>;
  }

  getLastKnownAvailableBalance(publicToken: string): Observable<QuickBalance> {
    return this.http.get(this.apiPath + publicToken + '/balance/last-known') as Observable<QuickBalance>;
  }

  getCardPinStatus(publicToken: string): Observable<Pin> {
    return this.http.get(this.apiPath + publicToken + '/get-pin-status') as Observable<Pin>;
  }

  getCardPerms(publicToken: string): Observable<string> {
    return this.http.get(this.apiPath + publicToken + '/get-card-perms') as Observable<string>;
  }

  getGpsCardStatus(publicToken: string): Observable<GpsCardStatus> {
    return this.http.get(this.apiPath + publicToken + '/get-gps-card-status', {headers:{'skip-error-snack':'true'} }) as Observable<GpsCardStatus>;
  }

  getGpsCardFeeGroup(publicToken: string): Observable<string> {
    return this.http.get(this.apiPath + publicToken + '/get-fee-group', {headers:{'skip-error-snack':'true'} }) as Observable<string>;
  }

  getGpsCardPermsGroup(publicToken: string): Observable<string> {
    return this.http.get(this.apiPath + publicToken + '/get-perms-group', {headers:{'skip-error-snack':'true'} }) as Observable<string>;
  }

  getGpsCardStatusCached(publicToken: string): Observable<GpsCardStatus> {
    return this.http.get(this.apiPath + publicToken + '/get-gps-card-status-cached', {headers:{'skip-error-snack': 'true'} }) as Observable<GpsCardStatus>;
  }

  getPendingExtension(publicToken: string): Observable<CardExtension|null> {
    return this.http.get(this.apiPath + publicToken + '/pending-extension', {headers:{'skip-error-snack': 'true'} }) as Observable<CardExtension|null>;
  }

  getPendingReorder(publicToken: string): Observable<CardReorder|null> {
    return this.http.get(this.apiPath + publicToken + '/pending-reorder', {headers:{'skip-error-snack': 'true'} }) as Observable<CardReorder|null>;
  }

  getCardExtensions(publicToken: string): Observable<CardExtension[]> {
    return this.http.get(this.apiPath + publicToken + '/extensions', {headers:{'skip-error-snack': 'true'} }) as Observable<CardExtension[]>;
  }

  extendCard(publicToken: string): Observable<CardExtension> {
    return this.http.post(this.apiPath + 'extend-card',
      {
        publicToken,
      }, {headers:{'skip-error-snack': 'true'} }) as Observable<CardExtension>;
  }

  queueForConversion(publicToken: string): Observable<any> {
    return this.http.post(this.apiPath + 'queue-for-conversion',
      {
        publicToken,
      }, {headers:{'skip-error-snack': 'true'} }) as Observable<any>;
  }

  issuePseudoCard(publicToken: string): Observable<CardIssueResultDto> {
    return this.http.post(this.apiPath + 'issue-pseudo-card',
      {
        publicToken,
      }, {headers:{'skip-error-snack': 'true'} }) as Observable<CardIssueResultDto>;
  }

  extendCards(cardIds: number[]): Observable<any> {
    return this.http.post(this.apiPath + 'extend-cards',
      cardIds, {headers:{'skip-error-snack': 'true'} }) as Observable<CardExtension>;
  }


  setGpsCardStatus(publicToken: string, gpsCardStatus: GpsCardStatus, confirmIrreversible: boolean, reason: string|null, reasonStatement: string|null, includeSecondaries: boolean|null): Observable<string> {
    return this.http.post(this.apiPath + publicToken + '/set-gps-card-status',
      {
        publicToken,
        newCardStatus: gpsCardStatus,
        confirmIrreversible,
        reason,
        reasonStatement,
        includeSecondaries: includeSecondaries,
      }, {headers:{'skip-error-snack': 'true'} }) as Observable<string>;
  }

  setGpsCardFees(publicToken: string, feeGroup: string): Observable<string> {
    return this.http.post(this.apiPath + publicToken + '/set-gps-card-fees',
      {
        publicToken,
        newFeeGroup: feeGroup,

      }, {headers:{'skip-error-snack': 'true'} }) as Observable<string>;
  }

  upgradeCard(publicToken: string, from: string, to: string): Observable<string> {
    return this.http.post(this.apiPath + 'upgrade-card',
      {
        publicToken,
        from,
        to
      }, {headers:{'skip-error-snack': 'true'} }) as Observable<string>;
  }

  getMany(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string,
    additionalFilterData?: string[],
    enumFilter?: { key: string; value: string},
    companiesFilter?: string[],

    additionalField1?: string,
    additionalField2?: string,
    additionalValue1?: string,
    additionalValue2?: string,
    identStatusFilter?: string[],
    productsFilter?: string[],
    cardStatusFilter?: string[],

    ): Observable<DataListModel<Card>> {


    let params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set('sortDirection', sortDirection)
      .set('startFilterDate', toServerDateString(startFilterDate))
      .set('endFilterDate', toServerDateString(endFilterDate))

    ;

    if (companiesFilter instanceof Array) {
      companiesFilter.forEach(element => {
        params = params.append('companiesFilter', element);
      });
    }

    if (productsFilter instanceof Array) {
      productsFilter.forEach(element => {
        params = params.append('productsFilter', element);
      });
    }

    if (cardStatusFilter instanceof Array) {
      cardStatusFilter.forEach(element => {
        params = params.append('cardStatusFilter', element);
      });
    }


    if (dateFilterCols instanceof Array) {
      dateFilterCols.forEach(element => {
        params = params.append('dateFilterCols', element);
      });
    }
    return this.http.get(this.apiPath, { params }) as Observable<DataListModel<Card>>;
  }

  getForCustomer( id: number ) {
    return this.http.get( this.apiPath + 'customer/' + id ) as Observable<Array<Card>>;
  }

  getCancellationReasons(): Observable<EnumModel[]> {
    return this.http.get(this.apiPath + 'cancellation-reasons') as Observable<EnumModel[]>;
  }

  getForOrder( id: number ) {
    return this.http.get( this.apiPath + 'order/' + id ) as Observable<Card>;
  }

  add(item: Card): Observable<Object> {
    throw new Error('Method not implemented.');
  }


  update(item: Card) {
    return this.http.put(this.apiPath, item) as Observable<Card>;
  }

  cancelCard(id: number, item: CardCancellationModel): Observable<Object> {
    return this.http.post(this.apiPath + id +'/cancel-card', item);
  }


  delete(id: any) {
    return this.deleteMany([id]);
  }
  restore(id: any) {
    return this.restoreMany([id]);
  }

  deleteMany(idList: any[]) {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });

    return this.http.delete(this.apiPath, { params: httpParams });
  }

  restoreMany(idList: any[]) {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });
    return this.http.patch(this.apiPath, null, { params: httpParams });
  }

  closeCards( idList: any[] ): Observable<Object>  {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append( 'list', id.toString() );
    } );
    return this.http.get( this.apiPath + 'close-cards', { params: httpParams } );
  }

  closeCard( publicToken: string, reason: string ): Observable<Object>  {
    return this.http.post(this.apiPath + publicToken + '/close-card',
      {
        publicToken,
        reason,
      }) as Observable<string>;
  }

  unCloseCard( publicToken: string, reason: string ): Observable<Object>  {
    return this.http.post(this.apiPath + publicToken + '/un-close-card',
      {
        publicToken,
        reason,
      }) as Observable<string>;
  }

  unCloseCards( idList: any[] ): Observable<Object>  {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append( 'list', id.toString() );
    } );
    return this.http.get( this.apiPath + 'un-close-cards', { params: httpParams } );
  }

  export(idList: any[]): Observable<HttpResponse<Blob>> {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('id', id.toString());
    });
    return this.http.get(this.apiPath + 'export', { params: httpParams, responseType: 'blob', observe: 'response' });
  }


  unblockPin(publicToken: string) {
    return this.http.post(this.apiPath + publicToken + '/unblock-pin',
      {
        publicToken,
        oh: 1,
      }) as Observable<string>;
  }

  activateCard(publicToken: string) {
    return this.http.post(this.apiPath + publicToken + '/activate',
      {
        publicToken,
        oh: 1,
      }) as Observable<string>;
  }

  getNextAnnualFees(start: number, count: number): Observable<AnnualFeeInfoModel[]> {

    const params = new HttpParams()
      .set('start', start.toString())
      .set('count', count.toString())
    ;

    return this.http.get(this.apiPath+'next-annual-fees', { params }) as Observable<AnnualFeeInfoModel[]>;
  }

  getNextCardExpiries(start: number, count: number): Observable<CardExpiryInfoModel[]> {

    const params = new HttpParams()
      .set('start', start.toString())
      .set('count', count.toString())
    ;

    return this.http.get(this.apiPath+'next-card-expiries', { params }) as Observable<CardExpiryInfoModel[]>;
  }

  getLastCardActivities(start: number, count: number, companiesFilter: string[], str: string|null): Observable<CardActivityInfoModel[]> {

    let cf = '';
    if ( companiesFilter ) {
      companiesFilter.forEach((v) => {
        cf += v + ',';
      });
      cf = cf.substring(0, cf.length -1);
    }

    const params = new HttpParams()
      .set('start', start.toString())
      .set('count', count.toString())
      .set('company', cf)
      .set('str', str != null ? str : '')
    ;

    return this.http.get(this.apiPath+'last-card-activities', { params }) as Observable<CardActivityInfoModel[]>;
  }

  executePendingExtensions(): Observable<any> {
    return this.http.post(this.apiPath+'execute-pending-extensions', { params: {oh: 1} }) as Observable<any>;
  }

  executePendingConversions(): Observable<any> {
    return this.http.post(this.apiPath+'execute-pending-conversions', { params: {oh: 1} }) as Observable<any>;
  }

  changeCardProduct(publicToken: string, newProductId: number): Observable<any> {
    return this.http.post(this.apiPath+'change-product', { publicToken, newProductId }) as Observable<any>;
  }

  getUpgradeCost(publicToken: string, productKey: string) {
    const params = new HttpParams()
      .set('publicToken', publicToken)
      .set('productKey', productKey)

    ;

    return this.http.get(this.apiPath+'upgrade-card/preview-cost', { params }) as Observable<number>;
  }

  cancelCardConversion(publicToken: string) {
    return this.http.post(this.apiPath+'cancel-card-conversion', { publicToken }) as Observable<any>;
  }

  linkCards(cardId: number, newPrimaryCardId: number): Observable<any> {
    return this.http.post(this.apiPath+'link-cards', { cardId, newPrimaryCardId }) as Observable<any>;
  }

  balanceTransfer(cardId: number, destinationCardId: number) {
    return this.http.post(this.apiPath+'balance-transfer', { cardId, destinationCardId }) as Observable<any>;
  }

  executeCreateCardBatch(batchId: number) {
    return this.http.post(this.apiPath+'execute-card-create-batch/' + batchId, { hello:'world' }) as Observable<any>;
  }
  resetFailedCreateCardBatch(batchId: number) {
    return this.http.post(this.apiPath+'reset-failed-create-batch/' + batchId, { hello:'world' }) as Observable<any>;
  }


  importSuccessCardsFromCardCreateBatch(batchId: number) {
    return this.http.post(this.apiPath+'card-create-batch/' + batchId + '/import-success', { hello:'world' }) as Observable<any>;
  }

  setLimitGroup(publicToken: string, newLimitGroup: string) {
    return this.http.post(this.apiPath + publicToken +'/limit-group', { publicToken, limitGroup: newLimitGroup }) as Observable<any>;
  }

  getLimitGroup(publicToken: string): Observable<string> {
    return this.http.get(this.apiPath + publicToken + '/limit-group') as Observable<string>;
  }

  cardsExpiringSoonFile(): Observable<HttpResponse<Blob>> {
    let httpParams = new HttpParams();

    return this.http.get(this.apiPath + 'cards-expiring-soon/csv', { params: httpParams, responseType: 'blob', observe: 'response' });
  }


  setWhiteList(publicToken: string, listCode: string) {
    return this.http.post(this.apiPath + publicToken +'/whitelist', { publicToken, listCode }) as Observable<any>;
  }
  setBlackList(publicToken: string, listCode: string) {
    return this.http.post(this.apiPath + publicToken +'/blacklist', { publicToken, listCode }) as Observable<any>;
  }


}
